import React, { useState } from 'react';
import { useGetIdentity } from 'react-admin';
import { Sparkles, Send, X, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { dismissWorkItem } from './workItemsApi';
import { useMessageDraft } from './contexts/MessageDraftContext';

export interface FeedWorkItem {
  id: string;
  /** The objective/kind id (agent_tasks.task_type). 'inbound_reply' items resolve
   *  once the family is replied to, so any composer send closes them. */
  task_type?: string;
  description?: string | null;
  metadata?: {
    drafted_action?: {
      channel?: 'sms' | 'staff';
      body?: string;
      to?: string | null;
    };
  } | null;
}

/**
 * A "Suggested reply" card pinned just above the feed composer. Surfaces the
 * agent's open SMS work item for this case IN CONTEXT — the board shows the same
 * `agent_tasks` row for the cross-case queue, so acting here keeps both in sync.
 *
 * "Use / edit" drops the draft into the composer (via MessageDraftContext) and
 * selects the recipient — the human still edits and sends by hand (no auto-send).
 * The parent closes the work item + records the review once the SMS is actually
 * sent. "Dismiss" rejects it outright. Renders nothing for non-SMS work items.
 */
export function FeedSuggestedReply({
  workItem,
  onUse,
  onResolved,
}: {
  workItem: FeedWorkItem;
  /** Called when the draft is loaded into the composer (parent tracks the id to
   *  close the item on the subsequent SMS send). */
  onUse: (id: string) => void;
  /** Called after a dismiss so the parent can hide the card optimistically. */
  onResolved: (id: string) => void;
}) {
  const { identity } = useGetIdentity();
  const { setDraftMessage, requestFocus } = useMessageDraft();
  const [busy, setBusy] = useState(false);

  const draft = workItem.metadata?.drafted_action;
  const body = draft?.body?.trim();
  if (draft?.channel !== 'sms' || !body) return null;

  const reviewer = identity
    ? { id: String(identity.id), name: identity.fullName }
    : undefined;

  const handleUse = () => {
    setDraftMessage(body);
    requestFocus({ channel: 'sms', recipientPhone: draft.to ?? undefined });
    onUse(workItem.id);
  };

  const handleDismiss = async () => {
    setBusy(true);
    try {
      await dismissWorkItem(workItem.id, reviewer);
      onResolved(workItem.id);
    } catch {
      setBusy(false);
    }
  };

  // Label the card by what the suggestion actually is — "reply" only for inbound
  // replies; proactive objective SMS (collect info, delivery, etc.) aren't replies.
  const headerLabel =
    workItem.task_type === 'inbound_reply' ? 'Suggested reply' : 'Suggested SMS';

  return (
    // Outer padding floats the bubble off the composer; the bordered, rounded,
    // shadowed inner card sets the suggestion clearly apart from the controls.
    <div className="px-3 sm:px-4 py-2.5">
      <div className="rounded-xl border border-primary/25 bg-muted/40 p-3 shadow-sm">
        <div className="mb-1.5 flex items-center gap-2">
          <Sparkles className="h-3.5 w-3.5 shrink-0 text-primary" />
          <span className="text-[11px] font-semibold uppercase tracking-wide text-primary">
            {headerLabel}
          </span>
          {workItem.description && (
            <span className="truncate text-[11px] text-muted-foreground">
              {workItem.description}
            </span>
          )}
        </div>
        {/* preserve the draft's line breaks / bullet list (whitespace-pre-wrap) —
            the body is stored with newlines and Aircall sends them as-is. */}
        <p className="max-h-48 overflow-y-auto whitespace-pre-wrap break-words rounded-lg bg-background px-3 py-2 text-sm leading-relaxed">
          {body}
        </p>
        <div className="mt-2.5 flex items-center gap-2">
          <Button
            size="sm"
            onClick={handleUse}
            disabled={busy}
            className="gap-1.5"
          >
            <Send className="h-3.5 w-3.5" />
            Use / edit
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={handleDismiss}
            disabled={busy}
            className="gap-1.5 text-muted-foreground"
          >
            {busy ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <X className="h-3.5 w-3.5" />
            )}
            Dismiss
          </Button>
        </div>
      </div>
    </div>
  );
}
